// Notification sound preference section on the Profile page.
// Uses Web Audio synthesis for zero-asset preview + persists choice via /auth/me/sound and localStorage.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/api/client";
import { Music, Volume2, VolumeX, Play, Check } from "lucide-react";
import { playNotificationSound, LOCAL_KEY as SOUND_KEY } from "@/lib/notificationSound";

const OPTIONS = [
  { id: "classic",  label: "كلاسيك (طلبية)", desc: "نغمة مزدوجة دافئة" },
  { id: "chime",    label: "جرس رنّان",       desc: "ثلاث نغمات صاعدة" },
  { id: "whistle",  label: "صافرة قصيرة",    desc: "نبرة واحدة عالية" },
  { id: "silent",   label: "صامت",           desc: "بدون صوت — إشعار بصري فقط" },
];

export default function SoundPreferenceSection({ user, onUpdated }) {
  const [current, setCurrent] = useState(() => localStorage.getItem(SOUND_KEY) || user?.notification_sound || "classic");
  const [busy, setBusy] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (user?.notification_sound && user.notification_sound !== current) {
      setCurrent(user.notification_sound);
      localStorage.setItem(SOUND_KEY, user.notification_sound);
    }
  }, [user?.notification_sound, current]);

  const select = async (id) => {
    setCurrent(id);
    localStorage.setItem(SOUND_KEY, id);
    setBusy(true);
    try {
      await api.patch("/auth/me/sound", { sound: id });
      onUpdated?.();
    } catch { /* ignore — local pref still applied */ }
    finally { setBusy(false); }
  };

  const preview = (id) => {
    setPreviewId(id);
    playNotificationSound(id, audioRef);
    setTimeout(() => setPreviewId(null), 800);
  };

  const activeLabel = useMemo(() => OPTIONS.find((o) => o.id === current)?.label || "كلاسيك", [current]);

  return (
    <div className="bg-white rounded-3xl talabya-shadow-soft p-5" data-testid="sound-pref-section">
      <div className="flex items-center gap-2 mb-1">
        <Music size={18} className="text-primary-brand"/>
        <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>نغمة الإشعارات</h2>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-4">اختر النغمة التي تسمعها عند وصول إشعار داخل التطبيق. الحالي: <span className="font-bold text-[var(--text-primary)]">{activeLabel}</span></p>

      <ul className="space-y-2">
        {OPTIONS.map((o) => {
          const active = current === o.id;
          const isSilent = o.id === "silent";
          return (
            <li
              key={o.id}
              className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-colors cursor-pointer ${active ? "border-primary-brand bg-blue-50" : "border-[var(--border)] bg-[var(--surface-alt)] hover:border-primary-brand/40"}`}
              onClick={() => !busy && select(o.id)}
              data-testid={`sound-opt-${o.id}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-primary-brand text-white" : "bg-white text-primary-brand"}`}>
                {isSilent ? <VolumeX size={18}/> : <Volume2 size={18}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm text-[var(--text-primary)]">{o.label}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">{o.desc}</div>
              </div>
              {!isSilent && (
                <button
                  onClick={(e) => { e.stopPropagation(); preview(o.id); }}
                  className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-black flex items-center gap-1 ${previewId === o.id ? "bg-green-500 text-white" : "bg-white text-primary-brand border border-primary-brand/30 hover:bg-blue-100"}`}
                  data-testid={`sound-preview-${o.id}`}
                >
                  <Play size={12}/> معاينة
                </button>
              )}
              {active && (
                <div className="w-6 h-6 rounded-full bg-primary-brand text-white flex items-center justify-center shrink-0" data-testid={`sound-active-${o.id}`}>
                  <Check size={13} strokeWidth={3}/>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
